import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import Winwheel from 'winwheel';
import { thunkCreateRewardInteraction } from '../../../store/actions/TouchpointActions';
import { thunkCreateSuccessNotification, thunkCreateInfoNotification, thunkCreateErrorNotification } from '../../../store/actions/NotificationActions';
import { IStore } from '../../../store/rootReducer';
import API from '../../../api/Api';
import ConfettiAnimation from '../shared/ConfettiAnimation';
import { CouponTemplateDto } from '../../../store/models/CouponTemplateDto';

// Load TweenMax from CDN
if (!(globalThis as any).TweenMax) {
  const script = document.createElement('script');
  script.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/2.1.3/TweenMax.min.js';
  script.async = true;
  document.head.appendChild(script);
}

type SpinWheelProps = ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const SpinWheel: React.FC<SpinWheelProps> = ({ thunkCreateRewardInteraction, thunkCreateSuccessNotification, thunkCreateInfoNotification, thunkCreateErrorNotification, customer, operationalUnits }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wheelRef = useRef<any>(null);
  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [couponTemplates, setCouponTemplates] = useState<{[key: string]: string}>({});

  const loadCouponTemplates = useCallback(async () => {
    try {
      const templatesData = await API.getCouponTemplates({ size: 100 });
      const templates = templatesData.data;
      
      const templateMap: {[key: string]: string} = {};
      
      // Find templates by name
      const giftTemplate = templates.find((t: CouponTemplateDto) => 
        t.name.includes('Glücksrad | Geschenk') || t.displayedCouponName?.includes('Glücksrad | Geschenk')
      );
      const fiveEuroTemplate = templates.find((t: CouponTemplateDto) => 
        t.name.includes('Glücksrad | 5 Euro Coupon') || t.displayedCouponName?.includes('Glücksrad | 5 Euro Coupon')
      );
      const tenPercentTemplate = templates.find((t: CouponTemplateDto) => 
        t.name.includes('Glücksrad | 10 % Coupon') || t.displayedCouponName?.includes('Glücksrad | 10 % Coupon')
      );
      
      if (giftTemplate) templateMap['Gift'] = giftTemplate.couponTemplateNumber;
      if (fiveEuroTemplate) templateMap['5€'] = fiveEuroTemplate.couponTemplateNumber;
      if (tenPercentTemplate) templateMap['10%'] = tenPercentTemplate.couponTemplateNumber;
      
      setCouponTemplates(templateMap);
      
      // Check if required templates are missing
      const missingTemplates = [];
      if (!giftTemplate) missingTemplates.push('Glücksrad | Geschenk');
      if (!fiveEuroTemplate) missingTemplates.push('Glücksrad | 5 Euro Coupon');
      if (!tenPercentTemplate) missingTemplates.push('Glücksrad | 10 % Coupon');
      
      if (missingTemplates.length > 0) {
        thunkCreateErrorNotification(
          'Fehlende Coupon-Vorlagen',
          `Die folgenden Coupon-Vorlagen wurden nicht gefunden: ${missingTemplates.join(', ')}`
        );
      }
    } catch (error) {
      console.error('Error loading coupon templates:', error);
      thunkCreateErrorNotification('Fehler beim Laden der Coupon-Vorlagen', error);
    }
  }, [thunkCreateErrorNotification]);

  const processCouponPrize = useCallback(async (prizeType: string, templateKey: string, errorMessage: string) => {
    if (couponTemplates[templateKey]) {
      await API.issueCouponToMe(couponTemplates[templateKey]);
      thunkCreateSuccessNotification(`🎉 "${prizeType}" coupon wurde auf dein Kundenkonto hinzugefügt!`);
    } else {
      thunkCreateErrorNotification('Coupon nicht verfügbar', errorMessage);
    }
  }, [couponTemplates, thunkCreateSuccessNotification, thunkCreateErrorNotification]);

  const processPointsPrize = useCallback(() => {
    const tradeAssociation = operationalUnits.find(unit => unit.unitType === 'TRADE_ASSOCIATION');
    if (customer && tradeAssociation) {
      thunkCreateRewardInteraction(
        customer.customerNumber,
        customer.externalCustomerId,
        tradeAssociation.externalUnitNumber,
        '100 Punkte beim Glücksrad'
      );
    }
  }, [customer, operationalUnits, thunkCreateRewardInteraction]);

  const alertPrize = useCallback(async function() {
    const winningSegment = wheelRef.current?.getIndicatedSegment();
    if (!winningSegment) return;

    if (winningSegment.text === 'Niete') {
      thunkCreateInfoNotification('🙁 Niete - Viel Glück beim nächsten Mal!');
      return;
    }

    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3000);

    try {
      switch (winningSegment.text) {
        case '5€':
          await processCouponPrize('5€', '5€', 'Die 5€ Coupon-Vorlage wurde nicht gefunden.');
          break;
        case '10%':
          await processCouponPrize('10%', '10%', 'Die 10% Coupon-Vorlage wurde nicht gefunden.');
          break;
        case '100 Pt':
          processPointsPrize();
          break;
        case 'Gift':
          await processCouponPrize('Geschenk', 'Gift', 'Die Geschenk Coupon-Vorlage wurde nicht gefunden.');
          break;
        default:
          thunkCreateSuccessNotification(`🎉 Gewonnen: ${winningSegment.text}!`);
      }
    } catch (error) {
      console.error('Error processing prize:', error);
      thunkCreateErrorNotification('Fehler beim Verarbeiten des Preises', error);
    }
  }, [thunkCreateInfoNotification, thunkCreateSuccessNotification, thunkCreateErrorNotification, processCouponPrize, processPointsPrize]);
  


  useEffect(() => {
    loadCouponTemplates();
  }, [loadCouponTemplates]);

  useEffect(() => {
    let checkTweenMaxInterval: NodeJS.Timeout | null = null;

    const initWheel = () => {
      if (canvasRef.current && (globalThis as any).TweenMax) {
        wheelRef.current = new (Winwheel as any)({
          canvasId: canvasRef.current.id,
          numSegments: 8,
          outerRadius: 200,
          drawText: false,
          textFontSize: 16,
          textOrientation: 'curved',
          textAlignment: 'inner',
          textMargin: 90,
          textFontFamily: 'monospace',
          textStrokeStyle: 'black',
          textLineWidth: 3,
          textFillStyle: 'white',
          drawMode: 'segmentImage',
          segments: [
            { image: '/gameZoneImages/spinWheel/coupon_5eur.png', text: '5€' },
            { image: '/gameZoneImages/spinWheel/niete.png', text: 'Niete'},
            { image: '/gameZoneImages/spinWheel/coupon_10percent.png', text: '10%' },
            { image: '/gameZoneImages/spinWheel/niete.png', text: 'Niete' },
            { image: '/gameZoneImages/spinWheel/bonuspoints_100.png', text: '100 Pt' },
            { image: '/gameZoneImages/spinWheel/niete.png', text: 'Niete' },
            { image: '/gameZoneImages/spinWheel/gift.png', text: 'Gift' },
            { image: '/gameZoneImages/spinWheel/niete.png', text: 'Niete' }
          ],
          animation: {
            type: 'spinToStop',
            duration: 5,
            spins: 8,
            callbackFinished: alertPrize
          }
        });
      }
    };

    if ((globalThis as any).TweenMax) {
      initWheel();
    } else {
      checkTweenMaxInterval = setInterval(() => {
        if ((globalThis as any).TweenMax) {
          if (checkTweenMaxInterval) {
            clearInterval(checkTweenMaxInterval);
            checkTweenMaxInterval = null;
          }
          initWheel();
        }
      }, 100);
    }

    return () => {
      if (checkTweenMaxInterval) {
        clearInterval(checkTweenMaxInterval);
      }
      if (wheelRef.current) {
        wheelRef.current = null;
      }
    };
  }, [alertPrize]);

  const startSpin = () => {
    if (!wheelSpinning && wheelRef.current) {
      wheelRef.current.animation.spins = 15; // High speed
      wheelRef.current.animation.callbackFinished = alertPrize;
      wheelRef.current.startAnimation();
      setWheelSpinning(true);
      
      setTimeout(() => {
        if (wheelRef.current) {
          alertPrize();
        }
      }, (wheelRef.current.animation.duration * 1000) + 500);
    }
  };

  const resetWheel = () => {
    if (wheelRef.current) {
      wheelRef.current.stopAnimation(false);
      wheelRef.current.rotationAngle = 0;
      wheelRef.current.draw();
      setWheelSpinning(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      position: 'relative',
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), url(/gameZoneImages/memoryGame/memory_back.jpg)',
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      minHeight: '80vh',
      overflow: 'hidden',
      padding: '20px'
    }}>
      <ConfettiAnimation show={showConfetti} />
      <div 
        style={{
          width: '421px',
          height: '564px',
          backgroundImage: 'url(/gameZoneImages/spinWheel/wheel_back.png)',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px'
        }}
      >
        <canvas ref={canvasRef} id="canvas" width="420" height="420">
          <p style={{ color: 'white', textAlign: 'center' }}>
            Sorry, your browser doesn't support canvas. Please try another.
          </p>
        </canvas>
      </div>
      <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
        <button 
          onClick={startSpin}
          disabled={wheelSpinning}
          className="btn btn-secondary"
          style={{ opacity: wheelSpinning ? 0.65 : 1 }}
        >
          Spin
        </button>
        <button 
          onClick={resetWheel}
          style={{ 
            background: 'none', 
            border: 'none', 
            color: '#8e8279',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IStore) => ({
  customer: state.customer.customer,
  operationalUnits: state.operationalUnit.operationalUnits
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => bindActionCreators({
  thunkCreateRewardInteraction,
  thunkCreateSuccessNotification,
  thunkCreateInfoNotification,
  thunkCreateErrorNotification
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SpinWheel);